/**
 * Turn a photo/scan of a handwritten signature into a clean, industry-standard
 * transparent PNG: paper background removed, ink darkened, auto-cropped to the
 * signature's bounding box.
 *
 * Output is always image/png so it composites cleanly on white prescription /
 * invoice paper without showing any paper texture from the source photo.
 */
export async function processSignatureImage(file: File): Promise<File> {
  const img = await loadImage(file);

  // Phones produce 4000+ px images. Cap the working canvas so processing is
  // fast and the final upload stays well under 2 MB.
  const MAX_INPUT_DIM = 2000;
  let workW = img.naturalWidth;
  let workH = img.naturalHeight;
  if (workW > MAX_INPUT_DIM || workH > MAX_INPUT_DIM) {
    const scale = MAX_INPUT_DIM / Math.max(workW, workH);
    workW = Math.round(workW * scale);
    workH = Math.round(workH * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = workW;
  canvas.height = workH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context not available');
  ctx.drawImage(img, 0, 0, workW, workH);

  const imageData = ctx.getImageData(0, 0, workW, workH);
  const { data } = imageData;

  // Pixels brighter than BG_THRESHOLD become fully transparent.
  // Pixels darker than INK_THRESHOLD become fully opaque ink.
  // Mid-tones get a smooth alpha so anti-aliased ink edges stay soft.
  const BG_THRESHOLD = 200;
  const INK_THRESHOLD = 110;
  const RANGE = BG_THRESHOLD - INK_THRESHOLD;

  // Only pixels above this final alpha contribute to the auto-crop bounding
  // box — keeps faint paper bleed-through from expanding the crop.
  const BBOX_ALPHA_MIN = 80;

  let minX = workW;
  let minY = workH;
  let maxX = -1;
  let maxY = -1;

  for (let i = 0; i < data.length; i += 4) {
    const originalAlpha = data[i + 3];

    // Already-transparent pixels (e.g. user uploaded a transparent PNG) stay
    // transparent — don't reprocess them as ink.
    if (originalAlpha < 30) {
      data[i + 3] = 0;
      continue;
    }

    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    let alpha: number;
    if (luminance >= BG_THRESHOLD) {
      alpha = 0;
    } else if (luminance <= INK_THRESHOLD) {
      alpha = 255;
      // Boost ink darkness so the signature reads strongly on white paper.
      data[i] = Math.max(0, r - 30);
      data[i + 1] = Math.max(0, g - 30);
      data[i + 2] = Math.max(0, b - 30);
    } else {
      alpha = Math.round((255 * (BG_THRESHOLD - luminance)) / RANGE);
    }
    data[i + 3] = alpha;

    if (alpha >= BBOX_ALPHA_MIN) {
      const pixelIndex = i >> 2;
      const x = pixelIndex % workW;
      const y = (pixelIndex - x) / workW;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  // No detectable ink — fall back to the full processed image so the user
  // still gets visible feedback instead of an empty file.
  if (maxX < minX || maxY < minY) {
    minX = 0;
    minY = 0;
    maxX = workW - 1;
    maxY = workH - 1;
  }

  const padding = Math.max(8, Math.round(Math.max(workW, workH) * 0.02));
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(workW - 1, maxX + padding);
  maxY = Math.min(workH - 1, maxY + padding);

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;

  // Cap output width so the final PNG is reasonably small.
  const MAX_OUTPUT_W = 1000;
  const outScale = cropW > MAX_OUTPUT_W ? MAX_OUTPUT_W / cropW : 1;
  const outW = Math.max(1, Math.round(cropW * outScale));
  const outH = Math.max(1, Math.round(cropH * outScale));

  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const outCtx = out.getContext('2d');
  if (!outCtx) throw new Error('Canvas 2D context not available');
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, outW, outH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    out.toBlob(b => {
      if (b) resolve(b);
      else reject(new Error('Failed to encode signature PNG'));
    }, 'image/png');
  });

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'signature';
  return new File([blob], `${baseName}.png`, { type: 'image/png' });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the selected image'));
    };
    img.src = url;
  });
}
